import { Card, CardContent, CardHeader, Input, ScrollArea, Switch } from '@ibsys/kit';
import { useQueryClient } from '@tanstack/react-query';
import { FC, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { expertReportKeys, useExpertReport, type ExpertReportResponse } from '@/entities/expert-report';
import {
  expertReportKmKeys,
  findKmPointsRuleOwner,
  useDeleteKmGroup,
  useKmGroup,
  useKmStructure,
  useUpdateKmGroup,
  type KmGroupResponse,
  type KmStructureResponse,
} from '@/entities/expert-report-km';
import { useHasPermission } from '@/entities/profile';
import { PointsRuleInput, type PointsRuleValue } from '@/features/expert-report/PointsRuleInput/PointsRuleInput';
import { usePointsRuleChangeWarning } from '@/features/expert-report/usePointsRuleChangeWarning';
import { ConfirmationButton, SaveButton } from '@/shared/ui';

type KmGroupCardProps = {
  groupId: number;
  onDeleted: () => void;
  className?: string;
};

type KmGroupFormValues = {
  name: string;
  isHeader: boolean;
  pointsRule: PointsRuleValue;
};

/** Правило расчёта группы в том виде, в каком его правит форма */
const mapGroupToFormPointsRule = (group: KmGroupResponse): PointsRuleValue => ({
  dsl: group.pointsRule?.dsl ?? '',
  dslHuman: group.pointsRule?.dslHuman ?? '',
});

/**
 * Карточка группы структуры заключения на модели знаний. Группа с вложенными группами
 * оценку не несёт; структурная группа-заголовок отмечается отдельным признаком.
 */
export const KmGroupCard: FC<KmGroupCardProps> = ({ groupId, onDeleted, className }) => {
  const { t } = useTranslation(['common', 'expertReport']);
  const queryClient = useQueryClient();
  const { data: group, isLoading } = useKmGroup(groupId);
  const reportId = group?.reportId;
  // Правило владельца написано по элементам структуры, а итог считает правило самого заключения:
  // по ним и видно, чьё правило требует внимания после правки группы
  const { data: report } = useExpertReport(reportId);
  const { data: structure } = useKmStructure({ reportId: reportId ?? 0 }, { enabled: Boolean(reportId) });
  const { mutateAsync: updateGroup, isPending: isUpdating } = useUpdateKmGroup();
  const { mutateAsync: deleteGroup, isPending: isDeleting } = useDeleteKmGroup();
  const allowAdmin = useHasPermission('ADMIN_EXPERT_REPORT');
  const warnAboutPointsRule = usePointsRuleChangeWarning('group');

  const form = useForm<KmGroupFormValues>({
    defaultValues: { name: '', isHeader: false, pointsRule: { dsl: '', dslHuman: '' } },
  });
  const { control, reset, handleSubmit, formState } = form;

  useEffect(() => {
    if (group) reset({ name: group.name, isHeader: group.isHeader, pointsRule: mapGroupToFormPointsRule(group) });
  }, [group, reset]);

  /**
   * Правило владельца после правки группы: она могла оставить его неприменимым либо считающим
   * результат не по всему составу вложенных. Сохранение обновляет кеш до того, как завершится,
   * поэтому владельца берём из кеша — уже в новом состоянии.
   */
  const warnAboutOwnerPointsRule = () => {
    if (!group || !reportId) return;
    const savedReport = queryClient.getQueryData<ExpertReportResponse>(expertReportKeys.detail(reportId)) ?? report;
    const savedStructure =
      queryClient.getQueryData<KmStructureResponse>(expertReportKmKeys.structure({ reportId })) ?? structure;
    if (!savedReport || !savedStructure) return;
    warnAboutPointsRule([findKmPointsRuleOwner(savedReport, savedStructure, group.parentId)]);
  };

  const onSubmit = async (values: KmGroupFormValues) => {
    await updateGroup({
      groupId,
      body: {
        name: values.name.trim(),
        isHeader: values.isHeader,
        // Правило уходит вместе с остальными полями: недостающую форму достраивает сервер,
        // а вернувшееся без правок правило он оставляет как есть
        pointsRule: values.pointsRule,
      },
    });
    warnAboutOwnerPointsRule();
  };

  const handleDelete = async () => {
    await deleteGroup(groupId);
    warnAboutOwnerPointsRule();
    onDeleted();
  };

  if (isLoading) {
    return (
      <Card className="w-full h-fit">
        <CardHeader>
          <div className="text-muted-foreground">{t('common:loading')}</div>
        </CardHeader>
      </Card>
    );
  }

  if (!group) {
    return (
      <Card className="w-full h-fit">
        <CardHeader>
          <div className="text-muted-foreground">{t('expertReport:selectItem')}</div>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader className="space-y-4">
        <Input
          control={control}
          name="name"
          label={t('expertReport:km.groupName')}
          disabled={!allowAdmin}
          rules={{
            required: { value: true, message: t('common:required') },
            // Имя из одних пробелов required не отсекает, а сохранять группу с таким именем нельзя
            validate: (value) => (typeof value === 'string' && value.trim().length > 0) || t('common:required'),
          }}
        />

        <Switch control={control} name="isHeader" label={t('expertReport:isHeader')} disabled={!allowAdmin} />

        <PointsRuleInput
          control={control}
          name="pointsRule"
          issues={group.pointsRule?.issues}
          disabled={!allowAdmin}
          isProcessing={isUpdating}
        />

        {allowAdmin && (
          <SaveButton
            className="justify-end"
            isDirty={formState.isDirty}
            isLoading={isUpdating}
            onClick={handleSubmit(onSubmit)}
            secondaryAction={
              <ConfirmationButton
                onConfirm={handleDelete}
                isLoadingConfirm={isDeleting}
                title={t('expertReport:km.removeGroup')}
                description={
                  <Trans
                    i18nKey="expertReport:km.removeGroupDescription"
                    values={{ groupName: group.name }}
                    components={{ strong: <strong /> }}
                  />
                }
                trigger={{
                  variant: 'outline',
                  color: 'destructive',
                  children: t('expertReport:km.deleteGroup'),
                }}
              />
            }
          />
        )}
      </CardHeader>

      <CardContent className="min-h-0 overflow-hidden">
        <ScrollArea className="h-full">
          <p className="text-sm text-muted-foreground">{t('expertReport:km.groupHint')}</p>
        </ScrollArea>
      </CardContent>
    </Card>
  );
};
